import { Injectable } from '@angular/core';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants, TealiumToolState } from '@ddt-web/analytics';
import { ApplicationPathConstants, PortfolioEditorMode } from '@ddt-web/config';
import { AppStoreConfig, DataUserDataService } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { Config, StoreService } from '@ddt-web/store-service';
import { UrlRedirectService, UrlService } from '@ddt-web/url';
import { PartnerConstants, PartnerUserProfileService } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as partition from 'lodash/partition';
import { first } from 'rxjs/operators';

import { ReimportDialogComponent } from '../alerts/reimport-dialog/reimport-dialog.component';
import { PartnerAnalyticsUtil } from '../alerts/utils/partner-analytics-util';
import { PartnerPortfolioFlagUtils } from '../alerts/utils/partner-portfolio-flag-utils';
import { PartnerPortfolioUtil } from '../alerts/utils/partner-util';
import { PartnerPortfolioDataService } from '../data-service/partner-portfolio-data.service';

@Injectable({
	providedIn: 'root'
})
export class PartnerInitService {

	constructor(
		private dialogService: DialogService,
		private urlService: UrlService,
		private urlRedirectService: UrlRedirectService,
		private partnerUserProfileService: PartnerUserProfileService,
		private storeService: StoreService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private dataUserDataService: DataUserDataService,
		private partnerPortfolioDataService: PartnerPortfolioDataService
	) { }

	/**
	 * Initialise checking partner based on portfolio ID from URL
	 *
	 * @param storeConfig The app store config
	 */
	public initWithPortfolioId(storeConfig: AppStoreConfig, shouldRedirectOnError: boolean = true): void {
		if (this.urlService.getRouteLocation().includes(ApplicationPathConstants.PORTFOLIO_EDITOR_PATH)) {
			return;
		}
		const portfolioIds = this.urlService.getPortfolioIds() || [];
		const partnerPortfolioIds = portfolioIds.filter(id => PartnerPortfolioUtil.isPartnerPortfolio(id));

		// setting partner name in globalAnalyticsParams
		this.partnerUserProfileService
			.getPartner()
			.pipe(first())
			.subscribe(partner => GlobalAnalyticsMapperUtil.setPartnerName(partner));

		if (isEmpty(partnerPortfolioIds)) {
			// if there is no partner portfolio, set partner portfolio type to empty string and return
			if (GlobalAnalyticsMapperUtil.getPartnerPortfolioType()) {
				GlobalAnalyticsMapperUtil.setPartnerPortfolioType('');
			}
			return;
		}

		this.storeService.selectByIds(storeConfig.associatedPortfolios, partnerPortfolioIds)
			.pipe(first(associatedPortfolios => {
				const associatedPortfoliosIds = get(associatedPortfolios, 'ids', []);
				const allPortfoliosUpdatedInStore = partnerPortfolioIds.every(partnerId => associatedPortfoliosIds.includes(partnerId));
				const hasFailedPortfolio = partnerPortfolioIds.some(id => get(associatedPortfolios.entities[id], 'errorCode') || get(associatedPortfolios.entities[id], 'error'));

				const isValidPartnerIds = associatedPortfoliosIds.length && allPortfoliosUpdatedInStore && !hasFailedPortfolio;
				return associatedPortfolios && !associatedPortfolios.isLoading && isValidPartnerIds;
			}))
			.subscribe(associatedPortfolios => {
				const portfolios = Object.values(get(associatedPortfolios, 'entities', {}));

				// Check if number of portfolios from URL is only 1 before we inspect
				if (portfolioIds.length === 1) {
					this.inspectPartnerPortfolio(portfolios[0], storeConfig, shouldRedirectOnError);
				}
			});
	}

	/**
	 * Trigger different workflows based on the portfolio's errors
	 *
	 * @param portfolio The portfolio
	 * @param storeConfig The app store config
	 * @return true if clean, false if error
	 */
	private inspectPartnerPortfolio(portfolio: any, storeConfig: AppStoreConfig, shouldRedirectOnError: boolean = true): boolean {

		const isReimportRequired = get(portfolio, PartnerConstants.PARTNER_PORTFOLIO_FLAGS.SHOULD_REIMPORT, false);
		GlobalAnalyticsMapperUtil.setPartnerPortfolioType(get(portfolio, 'originPlatform.portfolioType', ''));

		// For old dirty portfolios - portfolios imported pre UMA workflow, then force a reimport
		const flags = get(portfolio, 'flags');
		const hasOldFlags = PartnerPortfolioFlagUtils.hasReallocatedSecurities(flags) || PartnerPortfolioFlagUtils.hasCashSubstitutions(flags);
		const isOldDirtyPortfolio = get(flags, 'isProcessed') && hasOldFlags;
		if (isOldDirtyPortfolio) {
			this.urlService.addQueryParamAndReload(PartnerConstants.PARTNER_REIMPORT_QUERY_PARAM, 'true');
			return false;
		}


		// if partner partfolio is a candidate for reimport, show the reimport dialog
		if (isReimportRequired) {
			this.showReimportDialog(portfolio['id'], storeConfig);
			return false;
		} else if (shouldRedirectOnError) {
			// if partner portfolio has errors, then redirect to portfolio editor and show unsupported import dialog
			if (PartnerPortfolioFlagUtils.hasErrors(flags)) {

				const partnerPortfoioErrors = PartnerAnalyticsUtil.getPartnerPortfolioComposition(portfolio);
				// imported portfolio has errors - tealium tracking for dirty portfolio
				this.tealiumAnalyticsService.trackEventTealium(
					new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.DIRTY_IMPORT, { dirtyPortfolioErrors: partnerPortfoioErrors })
				);
				this.urlRedirectService.redirectToPortfolioEditorView({ mode: PortfolioEditorMode.EDIT, portfolioIds: portfolio['id'] });
				return false;
			} else {
				if (GlobalAnalyticsMapperUtil.getToolName() === TealiumConstants.TOOL_NAMES.SCENARIO_TESTER_TOOL) {
					GlobalAnalyticsMapperUtil.setToolState(TealiumToolState.STARTED);
				}
				this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.PARTNER_EVENTS.CLEAN_IMPORT, {}));
				return true;
			}
		}
		return true;
	}

	private showReimportDialog(portfolioId: any, storeConfig: AppStoreConfig): void {
		this.dialogService.open(ReimportDialogComponent, { data: { portfolioId: portfolioId, storeConfig: storeConfig } });
	}


	/**
	 * Populates the storeConfig for the provided ids (both internal and partner portfolio ids) with the portfolio details info.
	 *
	 * @param {string[]} ids array of id of portfolio missing data to be populated for
	 * @param storeConfig the store config to be populated
	 * @param partner partner id if missing portfolios are external
	 * @param queryParams the query parameters to check for reimport flag
	 */
	public populatePortfolioMissingDetailsData<T>(ids: string[], storeConfig: Config<T>, partner: string): void {
		const [internalPortfolioIdsMissingData, partnerPortfolioIdsMissingData] = partition(ids, id => !PartnerPortfolioUtil.isPartnerPortfolio(id));

		if (!isEmpty(internalPortfolioIdsMissingData)) {
			this.dataUserDataService.fetchPortfolioWithId(internalPortfolioIdsMissingData, storeConfig);
		}

		if (!isEmpty(partnerPortfolioIdsMissingData) && PartnerConstants.ACTIVATED_PARTNERS.includes(partner)) {
			const shouldReimportForPartner = this.urlService.queryParams.reimport === 'true';
			this.partnerPortfolioDataService.fetchExternalModel(storeConfig, partner, partnerPortfolioIdsMissingData, shouldReimportForPartner);
		}
	}

}
